import { useState } from 'react';
import flightSearchService from '../services/flightSearchService';
import toast from 'react-hot-toast';

const useFlightSearch = () => {
  const [flights, setFlights] = useState({
    outbound: [],
    return: [],
    segments: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [totalResults, setTotalResults] = useState({
    outbound: 0,
    return: 0,
    segments: 0
  });

  // Search flights
  const searchFlights = async (params) => {
    setLoading(true);
    setError(null);

    try {
      console.log('useFlightSearch: Calling flightSearchService.searchFlights with params:', params);
      const results = await flightSearchService.searchFlights(params);

      console.log('useFlightSearch: Got results:', results);

      setFlights({
        outbound: results.outboundFlights || [],
        return: results.returnFlights || [],
        segments: results.segments || []
      });

      setTotalResults({
        outbound: results.totalOutbound || 0,
        return: results.totalReturn || 0,
        segments: results.totalSegments || 0
      });

      setSearchParams(params);

      return results;
    } catch (err) {
      console.error('useFlightSearch: Error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to search flights';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get flight details
  const getFlightDetails = async (flightId) => {
    try {
      const response = await flightSearchService.getFlightDetails(flightId);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to get flight details';
      toast.error(errorMessage);
      throw err;
    }
  };

  // Clear search results
  const clearResults = () => {
    setFlights({ outbound: [], return: [] });
    setTotalResults({ outbound: 0, return: 0 });
    setError(null);
    setSearchParams(null);
  };

  // Filter flights based on criteria
  const filterFlights = (flights, filters) => {
    if (!flights || flights.length === 0) return flights;

    return flights.filter(flight => {
      // Lowest Pax — sort handled separately, skip here
      // Fastest Flight Time — sort handled separately, skip here

      // Time of day filter — supports multiple selections
      const activeTimes = [];
      if (filters.morning) activeTimes.push('morning');
      if (filters.afternoon) activeTimes.push('afternoon');
      if (filters.evening) activeTimes.push('evening');

      if (activeTimes.length > 0) {
        const rawDatetime = flight.departure?.datetime || flight.departure_date_time;
        if (!rawDatetime) return true;
        const hour = new Date(rawDatetime).getHours();

        const matchesTime = activeTimes.some(time => {
          if (time === 'morning') return hour >= 6 && hour < 12;
          if (time === 'afternoon') return hour >= 12 && hour < 18;
          if (time === 'evening') return hour >= 18;
          return false;
        });

        if (!matchesTime) return false;
      }

      return true;
    });
  };

  // Sort flights
  const sortFlights = (flights, sortBy = 'default') => {
    if (!flights || flights.length === 0) return flights;

    const sorted = [...flights];

    switch (sortBy) {
      case 'price_asc':
      case 'lowestPax':
        return sorted.sort((a, b) =>
          parseFloat(a.price || a.seat_price || a.block_price || 0) - parseFloat(b.price || b.seat_price || b.block_price || 0)
        );
      case 'price_desc':
        return sorted.sort((a, b) =>
          parseFloat(b.price || b.seat_price || b.block_price || 0) - parseFloat(a.price || a.seat_price || a.block_price || 0)
        );
      case 'departure_asc':
        return sorted.sort((a, b) =>
          new Date(a.departure?.datetime || a.departure_date_time) - new Date(b.departure?.datetime || b.departure_date_time)
        );
      case 'departure_desc':
        return sorted.sort((a, b) =>
          new Date(b.departure?.datetime || b.departure_date_time) - new Date(a.departure?.datetime || a.departure_date_time)
        );
      case 'duration_asc':
      case 'fastestFlightTime':
        return sorted.sort((a, b) =>
          (a.duration || a.duration_minutes || 0) - (b.duration || b.duration_minutes || 0)
        );
      case 'duration_desc':
        return sorted.sort((a, b) =>
          (b.duration || b.duration_minutes || 0) - (a.duration || a.duration_minutes || 0)
        );
      case 'airport_asc':
        return sorted.sort((a, b) =>
          (a.departure?.airport_code || a.departure?.airportCode || '').localeCompare(b.departure?.airport_code || b.departure?.airportCode || '')
        );
      case 'airport_desc':
        return sorted.sort((a, b) =>
          (b.departure?.airport_code || b.departure?.airportCode || '').localeCompare(a.departure?.airport_code || a.departure?.airportCode || '')
        );
      default:
        return sorted;
    }
  };

  return {
    flights,
    loading,
    error,
    searchParams,
    totalResults,
    searchFlights,
    getFlightDetails,
    clearResults,
    filterFlights,
    sortFlights
  };
};

export default useFlightSearch;